import type { Lap } from "./laps";
import type { LapLabel } from "@contract/types";
import { matchKeyToLaps } from "./correctionKey";

export interface Suspect {
  lapIndex: number;
  currentStroke: string;
  suggestedStroke: string;
  reasons: string[];
}

// Conservative tuning. A length is only flagged when multiple signals strongly
// disagree with its label AND it fits another stroke clearly better.
export const MIN_COHORT = 3; // need at least this many same-stroke peers to judge
export const MIN_SIGNALS = 2; // of {duration, strokeCount, tempo} must disagree
export const MOD_Z_THRESHOLD = 3.5; // Iglewicz-Hoaglin modified z-score cutoff

/** Bump whenever the suspect algorithm/thresholds change — stamped into every
 *  label's suggestion trace so training data stays attributable. */
export const DETECTOR_VERSION = 1;

// The strokes a user can pick in the set-detail picker (FIX_TILES in
// SetDetailSheet.svelte). A suggestion must be one of these, or the nudge
// would open a picker with nothing to tap. Keep in sync with FIX_TILES.
export const SUGGESTABLE_STROKES = new Set(["Freestyle", "Backstroke", "Breaststroke", "Butterfly"]);

const MAD_SCALE = 0.6745; // modified z-score scale constant
const EPS = 1e-6;

export function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

export function mad(xs: number[], med: number): number {
  if (xs.length === 0) return 0;
  return median(xs.map((x) => Math.abs(x - med)));
}

// Modified z-score. MAD == 0 (identical cohort) yields 0 so identical peers
// never manufacture an outlier on their own.
export function modZ(x: number, med: number, madVal: number): number {
  if (madVal <= EPS) return 0;
  return (MAD_SCALE * (x - med)) / madVal;
}

export interface Metrics {
  duration: number;
  strokeCount: number;
  tempo: number;
  swolf: number | null;
}

interface SigStat {
  med: number;
  mad: number;
}

export interface Stats {
  duration: SigStat;
  strokeCount: SigStat;
  tempo: SigStat;
  n: number;
}

// A length we can judge / use as a peer: present in the swim, not rest/phantom,
// not already user-corrected (diverged stroke), and carrying usable numbers.
export function eligible(lap: Lap): boolean {
  if (lap.effectiveExcluded) return false;
  if (lap.strokeStyle !== lap.watchStrokeStyle) return false; // user corrected it
  if (lap.strokeCount == null || lap.strokeCount <= 0) return false;
  if (lap.durationSeconds <= 0) return false;
  return true;
}

export function metricsOf(lap: Lap): Metrics | null {
  if (lap.durationSeconds <= 0 || lap.strokeCount == null || lap.strokeCount <= 0) return null;
  return {
    duration: lap.durationSeconds,
    strokeCount: lap.strokeCount,
    tempo: lap.strokeCount / lap.durationSeconds,
    swolf: lap.swolf,
  };
}

function sigStat(values: number[]): SigStat {
  const med = median(values);
  return { med, mad: mad(values, med) };
}

export function statsOf(ms: Metrics[]): Stats {
  return {
    duration: sigStat(ms.map((m) => m.duration)),
    strokeCount: sigStat(ms.map((m) => m.strokeCount)),
    tempo: sigStat(ms.map((m) => m.tempo)),
    n: ms.length,
  };
}

export function deviation(m: Metrics, s: Stats): {
  aggregate: number;
  high: { duration: boolean; strokeCount: boolean; tempo: boolean };
} {
  const zDur = modZ(m.duration, s.duration.med, s.duration.mad);
  const zStr = modZ(m.strokeCount, s.strokeCount.med, s.strokeCount.mad);
  const zTmp = modZ(m.tempo, s.tempo.med, s.tempo.mad);
  return {
    aggregate: Math.abs(zDur) + Math.abs(zStr) + Math.abs(zTmp),
    high: {
      duration: Math.abs(zDur) > MOD_Z_THRESHOLD,
      strokeCount: Math.abs(zStr) > MOD_Z_THRESHOLD,
      tempo: Math.abs(zTmp) > MOD_Z_THRESHOLD,
    },
  };
}

const BETTER_RATIO = 0.6; // another stroke must fit at least this much better than the label

function reasonsFor(
  m: Metrics,
  ownStats: Stats,
  high: { duration: boolean; strokeCount: boolean; tempo: boolean },
  stroke: string,
): string[] {
  const out: string[] = [];
  if (high.duration) {
    out.push(
      m.duration > ownStats.duration.med
        ? `slower than your other ${stroke} lengths`
        : `faster than your other ${stroke} lengths`,
    );
  }
  if (high.strokeCount) {
    out.push(
      m.strokeCount > ownStats.strokeCount.med
        ? `more strokes than your other ${stroke} lengths`
        : `fewer strokes than your other ${stroke} lengths`,
    );
  }
  if (out.length === 0 && high.tempo) {
    out.push(`a different rhythm than your other ${stroke} lengths`);
  }
  return out;
}

export function findStrokeSuspects(laps: Lap[]): Suspect[] {
  // Eligible peers grouped by labeled stroke (swim-wide).
  const byStroke = new Map<string, { lap: Lap; m: Metrics }[]>();
  for (const lap of laps) {
    if (!eligible(lap)) continue;
    const m = metricsOf(lap);
    if (!m) continue;
    const arr = byStroke.get(lap.strokeStyle) ?? [];
    arr.push({ lap, m });
    byStroke.set(lap.strokeStyle, arr);
  }

  const suspects: Suspect[] = [];

  for (const [stroke, members] of byStroke) {
    for (const { lap, m } of members) {
      // (own cohort) = same-stroke peers excluding this length
      const ownPeers = members.filter((x) => x.lap.index !== lap.index).map((x) => x.m);
      if (ownPeers.length < MIN_COHORT) continue; // can't judge -> quiet

      const ownStats = statsOf(ownPeers);
      const ownDev = deviation(m, ownStats);
      const highCount =
        (ownDev.high.duration ? 1 : 0) + (ownDev.high.strokeCount ? 1 : 0) + (ownDev.high.tempo ? 1 : 0);
      if (highCount < MIN_SIGNALS) continue; // (A) does not disagree with its own label

      // (B) does it fit another stroke clearly better?
      let bestStroke = "";
      let bestAgg = Infinity;
      for (const [otherStroke, otherMembers] of byStroke) {
        if (otherStroke === stroke) continue;
        if (!SUGGESTABLE_STROKES.has(otherStroke)) continue; // can't suggest non-picker strokes
        const otherPeers = otherMembers.map((x) => x.m);
        if (otherPeers.length < MIN_COHORT) continue;
        const agg = deviation(m, statsOf(otherPeers)).aggregate;
        if (agg < bestAgg) {
          bestAgg = agg;
          bestStroke = otherStroke;
        }
      }
      if (!bestStroke) continue; // no candidate -> no naked flag
      if (bestAgg > ownDev.aggregate * BETTER_RATIO) continue; // not clearly better

      suspects.push({
        lapIndex: lap.index,
        currentStroke: stroke,
        suggestedStroke: bestStroke,
        reasons: reasonsFor(m, ownStats, ownDev.high, stroke),
      });
    }
  }

  return suspects.sort((a, b) => a.lapIndex - b.lapIndex);
}

/**
 * 1-based lap indices carrying a "user-confirmed" label — "I looked, the watch
 * was right." deriveWorkout subtracts these from the suspects list, which is
 * the whole persist-until-reviewed mechanism: resolution state lives on the
 * persisted label, the queue is re-derived fresh every load, and a confirmed
 * length can never be re-flagged (no re-nag).
 *
 * Matching mirrors applyStrokeOverrides exactly: composite key when all three
 * fields are present (orphan → skip, never misapply), lapIndex for legacy
 * keyless labels.
 */
export function confirmedLapIndices(laps: Lap[], labels: LapLabel[]): Set<number> {
  const out = new Set<number>();
  if (labels.length === 0) return out;
  const lapKeys = laps.map((l) => l.correctionKey);

  for (const label of labels) {
    if (label.provenance !== "user-confirmed") continue;
    if (
      label.offsetSeconds != null &&
      label.durationSeconds != null &&
      label.duplicateOrdinal != null
    ) {
      const targetIdx = matchKeyToLaps(
        {
          offsetSeconds: label.offsetSeconds,
          durationSeconds: label.durationSeconds,
          duplicateOrdinal: label.duplicateOrdinal,
        },
        lapKeys,
      );
      if (targetIdx === null) continue;
      out.add(laps[targetIdx].index);
    } else {
      const lap = laps.find((l) => l.index === label.lapIndex);
      if (lap) out.add(lap.index);
    }
  }
  return out;
}
